import { isOneOf, templateTypeSchema } from '@tronche2tek/shared-types';
import { useTranslation } from 'react-i18next';

import type { updateTemplate } from '../../api/admin';
import { TemplateDefaultsPanel } from './TemplateDefaultsPanel';
import { TemplateSidesPanel } from './TemplateSidesPanel';
import type { CardTemplate } from './types';
import { fmt } from './utils';

export type TemplatePatch = Parameters<typeof updateTemplate>[1];

interface TemplateRowProps {
  template: CardTemplate;
  /** First row of the list — no rule above it. */
  first: boolean;
  sides: { id: string; name: string }[];
  isSidesPanelOpen: boolean;
  isDefaultsPanelOpen: boolean;
  onToggleSidesPanel: () => void;
  onToggleDefaultsPanel: () => void;
  onCloseSidesPanel: () => void;
  /** Resolves to whether the update went through. */
  onPatch: (data: TemplatePatch) => Promise<boolean>;
  onToggleActive: () => void;
  onDelete: () => void;
  onSaveDefaults: (defaults: Record<string, string>) => void;
}

/** One template in the list: summary, controls, and its two fold-out panels. */
export function TemplateRow({
  template: tmpl,
  first,
  sides,
  isSidesPanelOpen,
  isDefaultsPanelOpen,
  onToggleSidesPanel,
  onToggleDefaultsPanel,
  onCloseSidesPanel,
  onPatch,
  onToggleActive,
  onDelete,
  onSaveDefaults,
}: TemplateRowProps) {
  const { t } = useTranslation();
  const defaultsCount = tmpl.defaults ? Object.keys(tmpl.defaults).length : 0;
  const statusClass = tmpl.active ? 'epi-tag green' : 'epi-tag muted';
  const statusLabel = tmpl.active
    ? t('admin.templates.active')
    : t('admin.templates.inactive');
  const toggleActiveLabel = tmpl.active
    ? t('admin.templates.disable')
    : t('admin.templates.enable');

  return (
    <div
      className={!tmpl.active ? 'opacity-50' : ''}
      style={{
        borderTop: first ? 'none' : '1px solid var(--rule)',
      }}
    >
      <div className="flex items-center gap-4 px-4 py-3.5">
        <TemplateSummary template={tmpl} />
        <span className={statusClass}>{statusLabel}</span>
        <select
          value={tmpl.type}
          onChange={(e) => {
            const { value } = e.target;
            if (isOneOf(templateTypeSchema.options, value)) {
              void onPatch({ type: value });
            }
          }}
          className="px-1.5 py-1 text-xs"
          title={t('admin.templates.typeLabel')}
        >
          {(['student', 'employee', 'other'] as const).map((tp) => (
            <option key={tp} value={tp}>
              {t(`admin.templates.type.${tp}`)}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={onToggleSidesPanel}
          className={
            isSidesPanelOpen
              ? 'epi-tag coral cursor-pointer'
              : 'epi-tag cursor-pointer'
          }
        >
          {t('admin.templates.sides')}
        </button>
        <button
          type="button"
          onClick={onToggleDefaultsPanel}
          className={
            isDefaultsPanelOpen
              ? 'epi-tag blue cursor-pointer'
              : 'epi-tag cursor-pointer'
          }
        >
          {t('admin.templates.defaults')}
          {defaultsCount > 0 && (
            <span className="ml-1 opacity-70">({defaultsCount})</span>
          )}
        </button>
        <button
          type="button"
          onClick={onToggleActive}
          className="epi-tag cursor-pointer"
        >
          {toggleActiveLabel}
        </button>
        <DeleteTemplateButton onDelete={onDelete} />
      </div>

      {isSidesPanelOpen && (
        <TemplateSidesPanel
          template={tmpl}
          sides={sides}
          onChangeFront={(frontSideId) => {
            void onPatch({ frontSideId });
          }}
          onChangeBack={(backSideId) => {
            void onPatch({ backSideId }).then((ok) => {
              if (ok) {
                onCloseSidesPanel();
              }
            });
          }}
        />
      )}

      {isDefaultsPanelOpen && (
        <TemplateDefaultsPanel template={tmpl} onSave={onSaveDefaults} />
      )}
    </div>
  );
}

/** Name, description, and the recto/verso/creation tags. */
function TemplateSummary({ template: tmpl }: { template: CardTemplate }) {
  const { t } = useTranslation();
  return (
    <div className="min-w-0 flex-1">
      <p className="text-sm font-medium text-ink">{tmpl.name}</p>
      {tmpl.description && (
        <p className="mt-0.5 truncate text-xs text-muted-soft">
          {tmpl.description}
        </p>
      )}
      <div className="mt-1 flex flex-wrap items-center gap-2">
        {tmpl.frontSide ? (
          <span className="border border-blue/25 bg-blue/6 px-1.5 py-0.5 text-xs text-blue">
            {t('admin.templates.rectoLabel', {
              name: tmpl.frontSide.name,
            })}
          </span>
        ) : (
          <span className="text-xs text-muted-soft italic">
            {t('admin.templates.noFrontSide')}
          </span>
        )}
        {tmpl.backSide && (
          <span className="border border-coral/35 bg-coral/10 px-1.5 py-0.5 text-xs text-coral">
            {t('admin.templates.versoLabel', {
              name: tmpl.backSide.name,
            })}
          </span>
        )}
        <span className="text-xs text-muted-soft">{fmt(tmpl.createdAt)}</span>
      </div>
    </div>
  );
}

function DeleteTemplateButton({ onDelete }: { onDelete: () => void }) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onDelete}
      className="shrink-0 text-muted-soft transition-colors hover:text-magenta"
      title={t('admin.templates.delete')}
    >
      <svg
        className="size-4"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
        />
      </svg>
    </button>
  );
}
